<template>
  <div v-cloak>
    <div style="padding-left: 30px; padding-right: 30px;">
      <div class="col-lg-12 col-xl-8 grid-filter" v-if="showFilters">
        <filter-options v-bind:pageTitle="pageTitle" v-bind:filterItems="filterItems"
          v-on:filter-updated="filterInvoices" />
      </div>

      <div style="margin-top: 8px;">
        <b-table responsive show-empty :empty-text="$t('no_invoices_found')" ref="table" :busy.sync="isBusy"
          :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :current-page="currentPage" :per-page="perPage"
          :items="invoices" :fields="fields">
          <template v-slot:table-busy>
            <div style="height: 32px;">
              <div class="mb-3" style="color: #18385f; position: absolute; left: 50%;">
                <b-spinner></b-spinner>
              </div>
            </div>
          </template>

          <template v-slot:cell(invoiceId)="data">
            <span>{{ data.item.invoiceId }}</span>
          </template>

          <template v-slot:cell(invoiceDate)="data">
            {{ getDateFormatted(data.item.invoiceDate) }}
          </template>

          <template v-slot:cell(invoiceAmount)="data">
            {{ formatCurrency(data.item.invoiceAmount) }}
          </template>

          <template v-slot:cell(actions)="data">
            <b-button size="sm" variant="primary" @click="downloadInvoice(data.item)" class="mr-2">
              {{ $t('download') }}
            </b-button>
          </template>
        </b-table>

        <div class="row" style="text-align: left;">
          <div class="col-sm-6 offset-sm-3">
            <b-pagination v-if="totalRows > 0" align="center" ref="bottomPagination" label-prev-page="first"
              :total-rows="totalRows" :per-page="perPage" v-model="currentPage" class="my-0"
              style="margin-top: 10px !important" />
          </div>
          <div class="col-sm-3">
            <div class="form-group row" style="margin-top: 10px;">
              <label for="inputPassword" class="col-sm-7 col-form-label text-right">{{ $t('lines_per_page') }}</label>
              <div class="col-sm-5">
                <b-form-select :options="pageOptions" v-model="perPage" />
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { user } from './../../user';
import { SharePointService } from './../../services/sharepoint.service';
import { bus } from './../../main.js';
import FilterOptions from '@/components/filter/FilterOptions.vue';

export default {
  name: 'InvoicesOverview',
  components: {
    filterOptions: FilterOptions
  },
  data: function () {
    return {
      pageTitle: 'invoices',
      showFilters: true,
      filterItems: [],
      isBusy: false,
      sortBy: 'invoiceDate',
      sortDesc: true,
      invoices: [],
      allInvoices: [], // Store all invoices for client-side filtering
      fields: getTableFields(this),
      totalRows: 0,
      currentPage: 1,
      perPage: calculateRowsOnScreen(),
      pageOptions: getRowsPerPageOptions(calculateRowsOnScreen()),
      selectedCompanyId: this.$cookie.get('companyId') || null,
      selectedCustomers: this.$cookie.get('selectedCustomers') ? JSON.parse(this.$cookie.get('selectedCustomers')) : [],
      activeFilters: {
        invoiceAccount: '',
        invoiceId: '',
        fromDate: null,
        toDate: this.$moment().toDate()
      },
      customerNameToAccountMap: {} // Map customer names to invoice accounts
    };

  },
  methods: {
    filterInvoices(filters) {
      // Store previous date filters to check if they changed
      const previousFromDate = this.activeFilters.fromDate;
      const previousToDate = this.activeFilters.toDate;

      // Update active filters based on filter items
      this.activeFilters = {
        invoiceAccount: '',
        invoiceId: '',
        fromDate: null,
        toDate: this.$moment().toDate()
    };

      filters.forEach(filter => {
        if (filter.id === 'invoiceAccount' && filter.value) {
          // Convert customer name back to invoice account for filtering
          this.activeFilters.invoiceAccount = this.customerNameToAccountMap[filter.value] || filter.value;
        }
        if (filter.id === 'invoiceId' && filter.value) {
          this.activeFilters.invoiceId = filter.value;
        }
        if (filter.id === 'fromDate' && filter.value) {
          this.activeFilters.fromDate = filter.value;
        }
        if (filter.id === 'toDate' && filter.value) {
          this.activeFilters.toDate = filter.value;
        }
      });

      // Reset to page 1 when filtering
      this.currentPage = 1;

      // Check if date filters changed - if so, reload from SharePoint
      const dateFiltersChanged = previousFromDate !== this.activeFilters.fromDate || 
                                 previousToDate !== this.activeFilters.toDate;

      if (dateFiltersChanged) {
        // Reload data from SharePoint with new date filters
        this.loadInvoices();
      } else {
        // Apply client-side filters only
        this.applyFilters();
      }
    },
    applyFilters() {
      let filtered = [...this.allInvoices];

      // Filter by invoice account (client-side)
      if (this.activeFilters.invoiceAccount) {
        filtered = filtered.filter(invoice => 
          invoice.invoiceAccount === this.activeFilters.invoiceAccount
        );
      }

      // Filter by invoice ID (client-side)
      if (this.activeFilters.invoiceId) {
        const searchTerm = this.activeFilters.invoiceId.toLowerCase();
        filtered = filtered.filter(invoice => 
          invoice.invoiceId && invoice.invoiceId.toLowerCase().includes(searchTerm)
        );
      }

      // Date filtering is now done server-side in SharePoint query

      this.invoices = filtered;
      this.totalRows = filtered.length;
    },
    downloadInvoice: async function (invoice) {
      try {
        const sharePointService = new SharePointService(user);
        await sharePointService.initialize();

        const blob = await sharePointService.downloadFile(
          invoice.listId,
          invoice.id,
          invoice.fileName
        );

        // Download the file
        this.downloadFile(blob, invoice.fileName);

      } catch (error) {
        console.error('Error downloading invoice:', error);
        this.$bvToast.toast('Failed to download invoice', {
          title: 'Error',
          variant: 'danger',
          solid: true
          });
      }
    },
    downloadFile(blob, fileName) {
        if (navigator.msSaveBlob) {
          // For Internet Explorer
        navigator.msSaveBlob(blob, fileName);
        } else {
          const url = window.URL.createObjectURL(blob);
          const link = window.document.createElement('a');
          link.href = url;
        link.setAttribute('download', fileName);
          window.document.body.appendChild(link);
          link.click();
          window.document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
    },
    getDateFormatted: function (date) {
      return (date === null || date === '') ? '' : this.$moment(date).format('DD/MM/YYYY');
    },
    convertFilterDateToISOFormat(fieldName, fromDate, toDate) {
      const parts = [];

      if (fromDate) {
        const fromUtc = this.$moment(fromDate).startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
        parts.push(`fields/${fieldName} ge '${fromUtc}'`);
      }

      if (toDate) {
        const toUtcExclusive = this.$moment(toDate).add(1, 'day').startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
        parts.push(`fields/${fieldName} lt '${toUtcExclusive}'`);
      }

      return parts;
    },
    formatCurrency: function (amount) {
      if (!amount) return '';
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount);
    },
    changeLanguageHandler() {
      this.fields = getTableFields(this);
    },
    async loadInvoices() {
      this.isBusy = true;

      try {
        const sharePointService = new SharePointService(user);
        await sharePointService.initialize();

        const listName = 'DM_Invoices';
        
        // Build query filters
        const filters = [];
        
        // Filter by CompanyId if set
        if (this.selectedCompanyId) {
          filters.push(`fields/CompanyId eq '${this.selectedCompanyId}'`);
        }
        
        // Filter by InvoiceAccount (selectedCustomers) if set
        if (this.selectedCustomers && this.selectedCustomers.length > 0 && !this.selectedCustomers.includes('*ALL*')) {
          const customerFilters = this.selectedCustomers.map(customerId => `fields/InvoiceAccount eq '${customerId}'`);
          if (customerFilters.length > 1) {
            filters.push(`(${customerFilters.join(' or ')})`);
          } else {
            filters.push(customerFilters[0]);
          }
        }
        
        // Filter by date range (server-side) - timezone safe
        filters.push(...this.convertFilterDateToISOFormat('InvoiceDate', this.activeFilters.fromDate, this.activeFilters.toDate));

        const query = filters.length > 0 ? filters.join(' and ') : '';
        
        const invoices = await this.fetchInvoicesFromSharePoint(sharePointService, listName, query);
        
        this.allInvoices = invoices;
        
        // Initialize filter items with unique invoice accounts
        this.initializeFilterItems();
        
        // Apply any active filters (client-side filters only)
        this.applyFilters();

      } catch (error) {
        console.error('Error loading invoices:', error);
        this.$bvToast.toast('Failed to load invoices', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.isBusy = false;
      }
    },
    initializeFilterItems() {
      // Create a map from customer names to invoice accounts and collect unique combinations
      const accountMap = {};
      this.allInvoices.forEach(invoice => {
        if (invoice.invoiceAccount && invoice.invoicingName) {
          accountMap[invoice.invoicingName] = invoice.invoiceAccount;
        }
      });
      
      // Store the mapping for filtering
      this.customerNameToAccountMap = accountMap;
      
      // Get sorted list of customer names
      const customerNames = Object.keys(accountMap).sort();
      
      // Find the current customer name if there's an active filter
      let currentCustomerName = '';
      if (this.activeFilters.invoiceAccount) {
        // Find the customer name for the current invoice account filter
        currentCustomerName = Object.keys(this.customerNameToAccountMap).find(
          name => this.customerNameToAccountMap[name] === this.activeFilters.invoiceAccount
        ) || '';
      }
      
      this.filterItems = [
        { 
          id: 'invoiceAccount', 
          label: this.$t('invoice_account'),
          value: currentCustomerName, 
          type: 'customDropdown',
          options: ['', ...customerNames]
        },
        { 
          id: 'invoiceId', 
          label: this.$t('invoice_number'),
          value: this.activeFilters.invoiceId, 
          type: 'input' 
        },
        { 
          id: 'fromDate', 
          label: this.$t('from_date'),
          value: this.activeFilters.fromDate, 
          type: 'date' 
        },
        { 
          id: 'toDate', 
          label: this.$t('to_date'),
          value: this.activeFilters.toDate, 
          type: 'date' 
        }
      ];
    },
    async fetchInvoicesFromSharePoint(sharePointService, listName, query) {
      const invoices = [];

        try {
          const listId = await sharePointService.getListIdByName(listName);
          if (!listId) {
            console.warn(`List '${listName}' not found.`);
          return invoices;
          }

          const items = await sharePointService.getItemsForInvoiceList(listId, query);
          if (!items || items.length === 0) {
            console.log(`No invoices found in list '${listName}'.`);
            return invoices;
          }

          return items.map(item => ({
            listId,
            id: item.id,
            invoiceId: item.fields.InvoiceId,
            invoiceDate: item.fields.InvoiceDate,
            invoiceAmount: item.fields.InvoiceAmount,
            invoiceAccount: item.fields.InvoiceAccount,
            invoiceName: item.fields.InvoiceName,
            invoicingName: item.fields.InvoicingName,
            companyId: item.fields.CompanyId,
            salesId: item.fields.SalesId,
            customerRef: item.fields.CustomerRef,
            documentType: item.fields.DocumentType,
            documentName: item.fields.DocumentName,
            contractNumber: item.fields.ContractNumber,
            paymentMethod: item.fields.PaymentMethod,
            fileName: item.fields.DocumentName || item.fields.FileLeafRef
        }));

      } catch (error) {
        console.error(`Error fetching invoices from list '${listName}':`, error);
      }

      return invoices;
    }
  },
  created() {
    bus.$on('languageChanged', this.changeLanguageHandler);
    this.loadInvoices();
  },
  beforeDestroy() {
    bus.$off('languageChanged', this.changeLanguageHandler);
  }
};

function calculateRowsOnScreen() {
  var tableHeight = window.innerHeight - 440;
  var maxRows = tableHeight / 40;

  if (maxRows < 3) {
    return 5;
  }

  return Math.floor(maxRows);
}

function getRowsPerPageOptions(defaultRows) {
  return [
    { value: 5, text: '5' },
    { value: 10, text: '10' },
    { value: 25, text: '25' },
    { value: 50, text: '50' },
    { value: 100, text: '100' }
  ];
}

function getTableFields(context) {
  return [
    {
      key: 'invoiceId',
      label: context.$t('invoice_number') || 'Invoice Number',
      sortable: true,
      thClass: 'th-200 tr-left custom-th',
      tdClass: 'tr-200 tr-left custom-tr'
    },
    {
      key: 'invoiceDate',
      label: context.$t('invoice_date') || 'Invoice Date',
      sortable: true,
      thClass: 'th-150 tr-left custom-th',
      tdClass: 'tr-150 tr-left custom-tr'
    },
    {
      key: 'invoiceAmount',
      label: context.$t('amount') || 'Amount',
      sortable: true,
      thClass: 'th-150 tr-right custom-th',
      tdClass: 'tr-150 tr-right custom-tr'
    },
    {
      key: 'invoicingName',
      label: context.$t('invoicing_name') || 'Customer',
      sortable: true,
      thClass: 'th-250 tr-left custom-th',
      tdClass: 'tr-250 tr-left custom-tr'
    },
    {
      key: 'invoiceAccount',
      label: context.$t('invoice_account') || 'Account',
      sortable: true,
      thClass: 'th-200 tr-left custom-th',
      tdClass: 'tr-200 tr-left custom-tr'
    },
    {
      key: 'customerRef',
      label: context.$t('customer_reference') || 'Reference',
      sortable: true,
      thClass: 'th-200 tr-left custom-th',
      tdClass: 'tr-200 tr-left custom-tr'
    },
    {
      key: 'actions',
      label: context.$t('actions') || 'Actions',
      sortable: false,
      thClass: 'th-150 tr-center custom-th',
      tdClass: 'tr-150 tr-center custom-tr'
    }
  ];
}
</script>

<style scoped>
.row {
  text-align: left;
}

.tr-right {
  text-align: right !important;
}

.tr-center {
  text-align: center !important;
}

.tr-left {
  text-align: left !important;
}
</style>
